import { createAsyncThunk } from "@reduxjs/toolkit";
import { get, post } from "../../../services/apiService";
import moment from "moment";
import {
  checkUserExists,
  createUserInKeycloak,
} from "../../../services/userService";
import { showSnackbar } from "../../snackbar/Snackbar.slice";

const ACQUIRER_CREATE = "acquirerForm/create";
const ACQUIRER_UPDATE = "acquirerForm/update";
const ACQUIRER_GET_BY_ID = "acquirerForm/getById";
const ACQUIRER_GET_CURRENCIES = "acquirerForm/getCurrencies";

export const createAcquirer = createAsyncThunk(
  ACQUIRER_CREATE,
  async (payload, { rejectWithValue, dispatch }) => {
    const { token, handleGoBack, acquirerData, email, withISO, ...rest } =
      payload;
    const url = "/enroll-acquirer";
    // Ensure withISO is treated as boolean (form may send boolean or string from switch)
    const isWithISO = withISO === true || withISO === "true";
    const rol_data = isWithISO ? "acquirer-iso" : "acquirer";
    try {
      const newPayload = {
        ...rest,
      };
      if (
        newPayload?.blokko?.amountTxProcessing &&
        typeof newPayload?.blokko?.amountTxProcessing === "number"
      ) {
        newPayload.blokko.amountTxProcessing =
          newPayload.blokko.amountTxProcessing.toString();
      }
      if (
        newPayload?.blokko?.percentTxProcessing &&
        typeof newPayload?.blokko?.percentTxProcessing === "number"
      ) {
        newPayload.blokko.percentTxProcessing =
          newPayload.blokko.percentTxProcessing.toString();
      }
      newPayload.blokko.effectiveDate = moment(new Date()).format("YYYYMMDD");
      newPayload.acquirer.countryCode = rest.acquirer.countryCode.toUpperCase();
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const sanitizedUsername = `${acquirerData.acquirerId}_${rol_data.replace(
        "-",
        "_"
      )}_${newPayload.acquirerName
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "")}`;
      const existingUser = await checkUserExists({
        username: sanitizedUsername,
        email,
        token,
        acquirerData,
      });

      if (existingUser) {
        dispatch(
          showSnackbar({
            message: "User already exists with this email or Acquirer Name!",
            severity: "error",
          })
        );
        throw new Error(
          "User already exists with this email or Acquirer Name!"
        );
      }

      const response = await post(url, newPayload, config);

      dispatch(
        showSnackbar({
          message: "Acquirer successfully saved!",
          severity: "success",
        })
      );

      const userData = {
        username: sanitizedUsername,
        email: email,
        emailVerified: false,
        enabled: true,
        credentials: [
          {
            type: "password",
            value: `${response.acquirerID}${rol_data.replace(
              "-",
              "_"
            )}123*`,
            temporary: true,
          },
        ],
        attributes: {
          "acquirer-rol-data": rol_data,
          "acquirer-id-data": response.acquirerID,
          "acquirer-name-data": response.acquirerName,
          "acquirer-currency-data": response.fiat.currencyCode,
        },
      };
      // TODO: is commented due to send email error and rollback fail to
      try {
        await createUserInKeycloak(userData, token, acquirerData);
      } catch (keycloakError) {
        console.error("Error creating user in Keycloak:", keycloakError);
        dispatch(
          showSnackbar({
            message: `Acquirer created, Acquirer ID: ${response.data.acquirerID}, but user not created because there is already a user with that email or name!`,
            severity: "error",
          })
        );
        throw new Error(
          `Acquirer created, Acquirer ID: ${response.data.acquirerID}, but user not created because there is already a user with that email or name!`
        );
      }

      handleGoBack();
      return { enrollmentCreated: response };
    } catch (error) {
      console.error("Error creating enrollment thunk:", error);
      dispatch(
        showSnackbar({
          message: `${error.response?.data || error.message}`,
          severity: "error",
        })
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getAcquirerById = createAsyncThunk(
  ACQUIRER_GET_BY_ID,
  async (payload, { rejectWithValue }) => {
    const { token, acquirerId } = payload;
    const url = `/acquirer/${acquirerId}`;
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await get(url, config);
      return {
        acquirerData: response,
        acquirerId: response.acquirerID,
      };
    } catch (error) {
      console.error("Error getting acquirer by id thunk:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAcquirer = createAsyncThunk(
  ACQUIRER_UPDATE,
  async (payload, { rejectWithValue, dispatch }) => {
    const { token, handleGoBack, acquirerId, ...rest } = payload;
    const url = "/update-acquirer";
    try {
      const newPayload = {
        ...rest,
      };
      if (
        newPayload?.blokko?.amountTxProcessing &&
        typeof newPayload?.blokko?.amountTxProcessing === "number"
      ) {
        newPayload.blokko.amountTxProcessing =
          newPayload.blokko.amountTxProcessing.toString();
      }
      if (
        newPayload?.blokko?.percentTxProcessing &&
        typeof newPayload?.blokko?.percentTxProcessing === "number"
      ) {
        newPayload.blokko.percentTxProcessing =
          newPayload.blokko.percentTxProcessing.toString();
      }
      // Preserve the existing effectiveDate if it exists, otherwise set current date
      if (!newPayload.blokko.effectiveDate) {
        newPayload.blokko.effectiveDate = moment(new Date()).format("YYYYMMDD");
      }
      newPayload.acquirer.countryCode = rest.acquirer.countryCode.toUpperCase();
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          acquirerID: acquirerId,
          acquirerName: rest.acquirerName,
        },
      };
      const response = await post(url, newPayload, config);
      dispatch(
        showSnackbar({
          message: "Acquirer successfully updated!",
          severity: "success",
        })
      );
      handleGoBack();
      return { enrollmentCreated: response };
    } catch (error) {
      console.error("Error creating enrollment thunk:", error);
      dispatch(
        showSnackbar({
          message: `${error.response?.data || error.message}`,
          severity: "error",
        })
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getCurrencies = createAsyncThunk(
  ACQUIRER_GET_CURRENCIES,
  async (payload, { rejectWithValue }) => {
    const { token } = payload;
    const url = `/currencies`;
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const { data } = await get(url, config);
      return {
        currencies: data.map(
          ({ currency_alpha3_code }) => currency_alpha3_code
        ),
      };
    } catch (error) {
      console.error("Error getting acquirer by id thunk:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
